import { supabase } from './supabase';
import { Apartment, VibeCheckQuestion } from '../types';
import * as Sentry from '@sentry/react';
import { logger } from './logger';
import { handleError } from './errorHandler';

export interface VibeCheckResponse {
  answer: string;
  isGap: boolean;
}

/**
 * Edge Function URL for vibe check API
 * In production, this should point to your Supabase Edge Function URL
 * Set VITE_EDGE_FUNCTION_URL in your environment variables
 */
const EDGE_FUNCTION_URL = import.meta.env.VITE_EDGE_FUNCTION_URL || '';

/**
 * Generate an AI answer based on apartment data
 * Uses Edge Function (secure) if URL is configured, otherwise falls back to simulated response
 * 
 * SECURITY: OpenAI API calls are now handled server-side to protect the API key
 */
export async function generateVibeAnswer(
  question: string,
  apartment: Apartment,
  existingQuestions: VibeCheckQuestion[] = []
): Promise<VibeCheckResponse> {
  // Check if question is offensive or irrelevant
  if (isInappropriateQuestion(question)) {
    return {
      answer: "I can only answer questions about the apartment and its features. Please ask me something about this listing!",
      isGap: false
    };
  }

  // Try to use Edge Function (secure server-side API call)
  if (EDGE_FUNCTION_URL) {
    try {
      return await generateEdgeFunctionAnswer(question, apartment, existingQuestions);
    } catch (error) {
      logger.error('Edge Function API error:', error);
      Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'edge-function' } });
      // Fall back to simulated response
    }
  }

  // Fallback: Simulated intelligent response (no AI, uses rule-based logic)
  logger.debug('Using simulated vibe check response (Edge Function not configured)');
  return generateSimulatedAnswer(question, apartment, existingQuestions);
}

/**
 * Check if a question is inappropriate
 */
function isInappropriateQuestion(question: string): boolean {
  const inappropriateKeywords = [
    'hack', 'illegal', 'drugs', 'weapon', 'scam', 'steal',
    'discriminat', 'racist', 'sexist', 'violence'
  ];
  
  const lowerQuestion = question.toLowerCase();
  return inappropriateKeywords.some(keyword => lowerQuestion.includes(keyword));
}

/**
 * Generate answer using Edge Function (secure server-side OpenAI call)
 * 
 * This approach keeps the OpenAI API key secure on the server side
 * and never exposes it to the client.
 */
async function generateEdgeFunctionAnswer(
  question: string,
  apartment: Apartment,
  existingQuestions: VibeCheckQuestion[]
): Promise<VibeCheckResponse> {
  const response = await fetch(`${EDGE_FUNCTION_URL}/vibe-check/answer`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question,
      apartment: {
        title: apartment.title,
        price: apartment.price,
        neighborhood: apartment.neighborhood,
        address: apartment.address,
        rooms: apartment.rooms,
        sqm: apartment.sqm,
        floor: apartment.floor,
        petFriendly: apartment.petFriendly,
        amenities: apartment.amenities,
        description: apartment.description,
        idealRenterDescription: apartment.idealRenterDescription,
        lease_duration: apartment.lease_duration,
        move_in_date: apartment.move_in_date,
        security_deposit: apartment.security_deposit,
        vaad_bayit: apartment.vaad_bayit,
        arnona: apartment.arnona,
        elevator: apartment.elevator,
        renovated: apartment.renovated,
        private_parking: apartment.private_parking,
        storage: apartment.storage,
        safety_room: apartment.safety_room,
        first_floor_bars: apartment.first_floor_bars,
        furnished: apartment.furnished,
        building_floors: apartment.building_floors,
        sun_direction: apartment.sun_direction,
        appliances: apartment.appliances,
      },
      existingQuestions: existingQuestions.map(q => ({
        question: q.question,
        ai_answer: q.ai_answer,
        landlord_answer: q.landlord_answer,
      })),
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Edge Function request failed');
  }

  const data = await response.json();
  return { 
    answer: data.answer, 
    isGap: data.isGap 
  };
}

/**
 * Generate a simulated intelligent answer
 */
function generateSimulatedAnswer(
  question: string,
  apartment: Apartment,
  existingQuestions: VibeCheckQuestion[] = []
): VibeCheckResponse {
  const lowerQuestion = question.toLowerCase();
  
  // First, check if there's a similar question with a landlord answer
  const similarQuestion = existingQuestions.find(q => {
    if (!q.landlord_answer) return false;
    const qLower = q.question.toLowerCase();
    // Simple similarity check - could be enhanced with better matching
    const keywords = lowerQuestion.split(' ').filter(w => w.length > 3);
    return keywords.some(keyword => qLower.includes(keyword));
  });
  
  if (similarQuestion && similarQuestion.landlord_answer) {
    return {
      answer: similarQuestion.landlord_answer,
      isGap: false
    };
  }
  
  // Try to match question to available data
  
  // Price-related questions
  if (lowerQuestion.includes('price') || lowerQuestion.includes('rent') || lowerQuestion.includes('cost')) {
    return {
      answer: `The monthly rent for this apartment is ₪${apartment.price?.toLocaleString() || 0}. This includes the base rent. Please ask the landlord about utilities and any additional fees.`,
      isGap: false
    };
  }
  
  // Pet-related questions
  if (lowerQuestion.includes('pet') || lowerQuestion.includes('dog') || lowerQuestion.includes('cat')) {
    if (apartment.petFriendly) {
      return {
        answer: "Yes! This apartment is pet-friendly. For specific details about pet policies, size restrictions, or fees, I can ask the landlord to provide more information.",
        isGap: true
      };
    } else {
      return {
        answer: "Unfortunately, this apartment does not allow pets.",
        isGap: false
      };
    }
  }
  
  // Amenities questions
  if (lowerQuestion.includes('amenities') || lowerQuestion.includes('amenity') || 
      lowerQuestion.includes('gym') || lowerQuestion.includes('pool') || 
      lowerQuestion.includes('parking') || lowerQuestion.includes('laundry')) {
    
    const amenityMatch = apartment.amenities.find(a => 
      lowerQuestion.includes(a.toLowerCase())
    );
    
    if (amenityMatch) {
      return {
        answer: `Yes! This apartment includes ${amenityMatch}. ${apartment.amenities.length > 1 ? `Other amenities include: ${apartment.amenities.filter(a => a !== amenityMatch).join(', ')}.` : ''}`,
        isGap: false
      };
    } else if (apartment.amenities.length > 0) {
      return {
        answer: `Available amenities include: ${apartment.amenities.join(', ')}. If you're looking for something specific, I can ask the landlord for more details!`,
        isGap: true
      };
    } else {
      return {
        answer: "I don't have detailed amenity information. Let me ask the landlord to provide a complete list!",
        isGap: true
      };
    }
  }
  
  // Size/rooms questions
  if (lowerQuestion.includes('room') || lowerQuestion.includes('bedroom') || 
      lowerQuestion.includes('size') || lowerQuestion.includes('sqm') || lowerQuestion.includes('square') || lowerQuestion.includes('meter')) {
    return {
      answer: `This apartment has ${apartment.rooms} ${apartment.rooms === 1 ? 'bedroom' : 'bedrooms'} and is ${apartment.sqm} square meters. ${apartment.description ? 'The layout offers ' + apartment.description.substring(0, 100) + '...' : ''}`,
      isGap: false
    };
  }
  
  // Location questions
  if (lowerQuestion.includes('location') || lowerQuestion.includes('neighborhood') || 
      lowerQuestion.includes('area') || lowerQuestion.includes('nearby') || 
      lowerQuestion.includes('walk') || lowerQuestion.includes('transit')) {
    return {
      answer: `This apartment is located in ${apartment.neighborhood}${apartment.address ? ' at ' + apartment.address : ''}. For specific details about nearby amenities, transportation, or the neighborhood vibe, I can ask the landlord to provide more information!`,
      isGap: true
    };
  }
  
  // Utilities questions
  if (lowerQuestion.includes('utilit') || lowerQuestion.includes('water') || 
      lowerQuestion.includes('electric') || lowerQuestion.includes('gas') || 
      lowerQuestion.includes('internet')) {
    return {
      answer: "I don't have that specific information about utilities, but I can ask the landlord to clarify what's included and what you'd need to set up!",
      isGap: true
    };
  }
  
  // Lease terms questions
  if (lowerQuestion.includes('lease') || lowerQuestion.includes('term') || 
      lowerQuestion.includes('contract') || lowerQuestion.includes('deposit') || 
      lowerQuestion.includes('move')) {
    const leaseInfo = [];
    if (apartment.lease_duration) {
      leaseInfo.push(`The lease duration is ${apartment.lease_duration} months`);
    }
    if (apartment.move_in_date) {
      leaseInfo.push(`Available from ${new Date(apartment.move_in_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`);
    }
    if (apartment.security_deposit) {
      leaseInfo.push(`Security deposit is ₪${apartment.security_deposit?.toLocaleString() || 0}`);
    }
    
    if (leaseInfo.length > 0) {
      return {
        answer: `${leaseInfo.join('. ')}. For more specific lease terms, I can ask the landlord!`,
        isGap: leaseInfo.length < 3
      };
    }
    return {
      answer: "I don't have detailed information about lease terms and move-in requirements. Let me ask the landlord to provide those details!",
      isGap: true
    };
  }
  
  // Costs/fees questions (maintenance, arnona)
  if (lowerQuestion.includes('maintenance') || lowerQuestion.includes('vaad') || 
      lowerQuestion.includes('arnona') || lowerQuestion.includes('municipal') ||
      lowerQuestion.includes('tax') || lowerQuestion.includes('fee')) {
    const costInfo = [];
    if (apartment.vaad_bayit) {
      costInfo.push(`Vaad Bayit (building maintenance) is ₪${apartment.vaad_bayit?.toLocaleString() || 0}/month`);
    }
    if (apartment.arnona) {
      costInfo.push(`Arnona (municipal tax) is ₪${apartment.arnona?.toLocaleString() || 0}/month`);
    }
    
    if (costInfo.length > 0) {
      return {
        answer: `${costInfo.join('. ')}.`,
        isGap: false
      };
    }
    return {
      answer: "I don't have specific information about additional fees. Let me ask the landlord for the maintenance and municipal tax details!",
      isGap: true
    };
  }
  
  // Elevator questions
  if (lowerQuestion.includes('elevator') || lowerQuestion.includes('lift') || lowerQuestion.includes('stairs')) {
    if (apartment.elevator !== undefined) {
      return {
        answer: apartment.elevator 
          ? `Yes, the building has an elevator.${apartment.floor ? ` The apartment is on floor ${apartment.floor}.` : ''}`
          : `No, this building does not have an elevator.${apartment.floor ? ` The apartment is on floor ${apartment.floor}.` : ''}`,
        isGap: false
      };
    }
    return {
      answer: "I don't have information about the elevator. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Furnished questions
  if (lowerQuestion.includes('furnish') || lowerQuestion.includes('furniture')) {
    if (apartment.furnished !== undefined) {
      return {
        answer: apartment.furnished 
          ? "Yes, this apartment comes fully furnished!"
          : "This apartment is unfurnished - you'll need to bring your own furniture.",
        isGap: false
      };
    }
    return {
      answer: "I don't have information about furnishing. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Safety room / mamad questions
  if (lowerQuestion.includes('safe') || lowerQuestion.includes('mamad') || lowerQuestion.includes('shelter') || lowerQuestion.includes('security')) {
    if (apartment.safety_room !== undefined) {
      return {
        answer: apartment.safety_room 
          ? "Yes, this apartment has a safety room (Mamad)."
          : "This apartment does not have a dedicated safety room (Mamad).",
        isGap: false
      };
    }
    return {
      answer: "I don't have information about safety features. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Storage questions
  if (lowerQuestion.includes('storage') || lowerQuestion.includes('basement') || lowerQuestion.includes('locker')) {
    if (apartment.storage !== undefined) {
      return {
        answer: apartment.storage 
          ? "Yes, this apartment includes a storage unit."
          : "This apartment does not include a storage unit.",
        isGap: false
      };
    }
    return {
      answer: "I don't have information about storage. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Sun/light direction questions
  if (lowerQuestion.includes('sun') || lowerQuestion.includes('light') || lowerQuestion.includes('direction') || lowerQuestion.includes('exposure')) {
    if (apartment.sun_direction) {
      return {
        answer: `The apartment faces ${apartment.sun_direction}, which affects the natural light throughout the day.`,
        isGap: false
      };
    }
    return {
      answer: "I don't have specific information about sun exposure. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Appliances questions
  if (lowerQuestion.includes('appliance') || lowerQuestion.includes('oven') || lowerQuestion.includes('washer') || 
      lowerQuestion.includes('dryer') || lowerQuestion.includes('dishwasher') || lowerQuestion.includes('fridge') ||
      lowerQuestion.includes('microwave')) {
    if (apartment.appliances && apartment.appliances.length > 0) {
      return {
        answer: `The apartment includes these appliances: ${apartment.appliances.join(', ')}.`,
        isGap: false
      };
    }
    return {
      answer: "I don't have a list of included appliances. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Renovated questions
  if (lowerQuestion.includes('renovate') || lowerQuestion.includes('new') || lowerQuestion.includes('condition') || lowerQuestion.includes('updated')) {
    if (apartment.renovated !== undefined) {
      return {
        answer: apartment.renovated 
          ? "Yes, this apartment has been recently renovated!"
          : "This apartment has not been recently renovated.",
        isGap: false
      };
    }
    return {
      answer: "I don't have information about the renovation status. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Building info questions
  if (lowerQuestion.includes('building') || lowerQuestion.includes('floors') || lowerQuestion.includes('stories')) {
    if (apartment.building_floors) {
      return {
        answer: `The building has ${apartment.building_floors} floors total.${apartment.floor ? ` This apartment is on floor ${apartment.floor}.` : ''}${apartment.elevator ? ' There is an elevator.' : ''}`,
        isGap: false
      };
    }
    return {
      answer: "I don't have detailed building information. Let me ask the landlord!",
      isGap: true
    };
  }
  
  // Noise/quiet questions
  if (lowerQuestion.includes('noise') || lowerQuestion.includes('noisy') || 
      lowerQuestion.includes('quiet') || lowerQuestion.includes('loud')) {
    return {
      answer: "I don't have specific information about noise levels, but I can ask the landlord to describe the typical noise environment and any soundproofing features!",
      isGap: true
    };
  }
  
  // Default response - knowledge gap
  return {
    answer: "That's a great question! I don't have that specific information in the listing, but I'll flag this for the landlord to answer.",
    isGap: true
  };
}

/**
 * Build context string from apartment data
 */
function buildApartmentContext(apartment: Apartment, existingQuestions: VibeCheckQuestion[] = []): string {
  let context = `
Title: ${apartment.title}
Price: ₪${apartment.price}/month
Location: ${apartment.neighborhood}${apartment.address ? ', ' + apartment.address : ''}
Bedrooms: ${apartment.rooms}
Square Meters: ${apartment.sqm} m²
Floor: ${apartment.floor !== undefined ? apartment.floor : 'Not specified'}
Pet Friendly: ${apartment.petFriendly ? 'Yes' : 'No'}
`;

  if (apartment.amenities.length > 0) {
    context += `Amenities: ${apartment.amenities.join(', ')}\n`;
  }

  // Lease Terms
  if (apartment.lease_duration) {
    context += `Lease Duration: ${apartment.lease_duration} months\n`;
  }
  if (apartment.move_in_date) {
    context += `Available From: ${new Date(apartment.move_in_date).toLocaleDateString()}\n`;
  }
  if (apartment.security_deposit) {
    context += `Security Deposit: ₪${apartment.security_deposit?.toLocaleString() || 0}\n`;
  }

  // Monthly Costs
  if (apartment.vaad_bayit) {
    context += `Vaad Bayit (building maintenance): ₪${apartment.vaad_bayit}/month\n`;
  }
  if (apartment.arnona) {
    context += `Arnona (municipal tax): ₪${apartment.arnona}/month\n`;
  }

  // Property Features
  const features = [];
  if (apartment.elevator) features.push('Elevator');
  if (apartment.renovated) features.push('Recently Renovated');
  if (apartment.private_parking) features.push('Private Parking');
  if (apartment.storage) features.push('Storage Unit');
  if (apartment.safety_room) features.push('Safety Room (Mamad)');
  if (apartment.first_floor_bars) features.push('Window Security Bars');
  if (apartment.furnished) features.push('Furnished');
  if (features.length > 0) {
    context += `Property Features: ${features.join(', ')}\n`;
  }

  // Building Info
  if (apartment.building_floors) {
    context += `Building Total Floors: ${apartment.building_floors}\n`;
  }
  if (apartment.sun_direction) {
    context += `Sun Direction: ${apartment.sun_direction}\n`;
  }
  if (apartment.appliances && apartment.appliances.length > 0) {
    context += `Included Appliances: ${apartment.appliances.join(', ')}\n`;
  }
  
  if (apartment.description) {
    context += `Description: ${apartment.description}\n`;
  }
  
  if (apartment.idealRenterDescription) {
    context += `Ideal Renter: ${apartment.idealRenterDescription}\n`;
  }

  // Add previously answered questions (especially landlord's enriched answers)
  const answeredQuestions = existingQuestions.filter(q => q.landlord_answer || q.ai_answer);
  if (answeredQuestions.length > 0) {
    context += `\nPreviously Answered Questions:\n`;
    answeredQuestions.forEach(q => {
      const answer = q.landlord_answer || q.ai_answer;
      // Only include if it's not a knowledge gap response
      if (answer && !answer.toLowerCase().includes("don't have") && !answer.toLowerCase().includes("ask the landlord")) {
        context += `Q: ${q.question}\nA: ${answer}\n\n`;
      }
    });
  }

  return context;
}

/**
 * Save a vibe check question to the database
 */
export async function saveVibeCheckQuestion(
  apartmentId: string,
  question: string,
  aiAnswer: string,
  isKnowledgeGap: boolean
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return false;
    }

    const { error } = await supabase
      .from('vibe_check_questions')
      .insert({
        apartment_id: apartmentId,
        renter_id: user.id,
        question: question,
        ai_answer: aiAnswer,
        is_knowledge_gap: isKnowledgeGap
      });

    if (error) {
      console.error('Error saving vibe check question:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in saveVibeCheckQuestion:', error);
    Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'save-question' } });
    return false;
  }
}

/**
 * Fetch vibe check questions for an apartment (landlord view)
 */
export async function fetchVibeCheckQuestions(apartmentId: string) {
  try {
    const { data, error } = await supabase
      .from('vibe_check_questions')
      .select('*')
      .eq('apartment_id', apartmentId)
      .order('is_knowledge_gap', { ascending: false })
      .order('ask_count', { ascending: false })
      .order('last_asked_at', { ascending: false });

    if (error) {
      console.error('Error fetching vibe check questions:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchVibeCheckQuestions:', error);
    Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'fetch-questions' } });
    return [];
  }
}

/**
 * Fetch unanswered question counts for multiple apartments in a single query
 * Optimized to replace N+1 query pattern
 * @param apartmentIds - Array of apartment IDs to fetch counts for
 * @returns Map of apartment ID to unanswered question count
 */
export async function fetchUnansweredQuestionCounts(
  apartmentIds: string[]
): Promise<Record<string, number>> {
  try {
    if (!apartmentIds || apartmentIds.length === 0) {
      return {};
    }

    // Single query with GROUP BY to count unanswered questions per apartment
    const { data, error } = await supabase
      .from('vibe_check_questions')
      .select('apartment_id')
      .in('apartment_id', apartmentIds)
      .is('landlord_answer', null);

    if (error) {
      console.error('Error fetching unanswered question counts:', error);
      return {};
    }

    // Count occurrences of each apartment_id
    const counts: Record<string, number> = {};
    apartmentIds.forEach(id => {
      counts[id] = 0; // Initialize all to 0
    });

    data?.forEach((row: { apartment_id: string }) => {
      counts[row.apartment_id] = (counts[row.apartment_id] || 0) + 1;
    });

    return counts;
  } catch (error) {
    console.error('Error in fetchUnansweredQuestionCounts:', error);
    Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'fetch-unanswered-counts' } });
    return {};
  }
}

/**
 * Fetch a renter's own questions for an apartment (renter view)
 * Returns questions ordered by creation date to reconstruct conversation
 */
export async function fetchRenterQuestionsForApartment(apartmentId: string) {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return [];
    }

    const { data, error } = await supabase
      .from('vibe_check_questions')
      .select('*')
      .eq('apartment_id', apartmentId)
      .eq('renter_id', user.id)
      .order('created_at', { ascending: true}); // Oldest first for conversation order

    if (error) {
      console.error('Error fetching renter questions:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error in fetchRenterQuestionsForApartment:', error);
    Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'fetch-renter-questions' } });
    return [];
  }
}

/**
 * Answer a vibe check question (landlord action)
 */
export async function answerVibeCheckQuestion(
  questionId: string,
  landlordAnswer: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('User not authenticated');
      return false;
    }

    const { error } = await supabase
      .from('vibe_check_questions')
      .update({
        landlord_answer: landlordAnswer,
        answered_at: new Date().toISOString(),
        answered_by: user.id,
        is_knowledge_gap: false // Mark as resolved
      })
      .eq('id', questionId);

    if (error) {
      console.error('Error answering vibe check question:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in answerVibeCheckQuestion:', error);
    Sentry.captureException(error, { tags: { feature: 'vibecheck', action: 'answer-question' } });
    return false;
  }
}

